<script lang="ts">
    import ReviewsRatingInput from "@apps/reviews/components/ReviewsRatingInput/ReviewsRatingInput.svelte";
    import FormManager from "@lib/FormManager/FormManager";
    import FormManagerController from "@lib/FormManager/FormManagerController.svelte";
    import FormManagerProvider from "@lib/FormManager/FormManagerProvider.svelte";
    import { createEventDispatcher, onMount } from "svelte";

    import Button from "@components/Buttons/Button.svelte";
    import Image from "@components/Image/Image.svelte";
    import Scrollable from "@components/Scrollable.svelte";
    import T from "@components/T.svelte";

    import { LEAVE_REVIEW_SOURCE } from "../../../constants";
    import PurchasedProductEntity from "../../../entities/PurchasedProductEntity";

    export let source: LEAVE_REVIEW_SOURCE | undefined;
    export let purchasedProducts: PurchasedProductEntity[];
    export let formManager: FormManager;

    const dispatch = createEventDispatcher();

    const handleRate = (productId: number) => {
        formManager.submit();
        dispatch("updateProductId", { productId });
    };

    onMount(() => {
        formManager.updateValue("rating", 0);
    });

    const shopifyObj = window.Shopify as unknown as any;
</script>

<FormManagerProvider {formManager}>
    <div class="gw-rv-form-suggestions">
        <h2 class="gw-rv-form-suggestions__title">
            {#if source === LEAVE_REVIEW_SOURCE.LEAVE_REVIEW_PAGE_WIDGET}
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="review_more_products"
                />
            {:else}
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="rate_purchased_products"
                />
            {/if}
        </h2>
        <div class="gw-rv-form-suggestions__scrollable-area">
            <Scrollable visible bottomShadow topShadow>
                <FormManagerController name="rating" let:field>
                    <ul
                        class="gw-rv-form-suggestions__product-list gw-list-reset-default-styles"
                    >
                        {#each purchasedProducts as product}
                            <li class="gw-rv-form-suggestions__product-item">
                                <div
                                    class="gw-rv-form-suggestions__product-image-block"
                                >
                                    <Image
                                        imgClassName="gw-rv-form-suggestions__product-image"
                                        src={product.imageUrl || ""}
                                        alt=""
                                        objectFit="cover"
                                    />
                                </div>
                                <div
                                    class="gw-rv-form-suggestions__product-info"
                                >
                                    <h4
                                        class="gw-rv-form-suggestions__product-title"
                                    >
                                        {product.title}
                                    </h4>
                                    <div
                                        class="gw-rv-form-suggestions__product-rating-input"
                                    >
                                        <ReviewsRatingInput
                                            iconWidth="2.375em"
                                            iconHeight="2.375em"
                                            value={field.value}
                                            on:change={(event) => {
                                                field.onChange(event);
                                                handleRate(product.productId);
                                            }}
                                        />
                                    </div>
                                </div>
                            </li>
                        {/each}
                    </ul>
                </FormManagerController>
            </Scrollable>
        </div>
        {#if source === LEAVE_REVIEW_SOURCE.LEAVE_REVIEW_PAGE_WIDGET}
            <div class="gw-rv-form-suggestions__actions">
                <Button url={shopifyObj.shopUrl} variant="link" size="large">
                    <T
                        namespace="reviews.ReviewsFormModalWidget"
                        translationKey="go_to"
                        translationOptions={{
                            link: shopifyObj.shopName,
                        }}
                    />
                </Button>
            </div>
        {/if}
    </div>
</FormManagerProvider>

<style lang="postcss">
    .gw-rv-form-suggestions {
        width: 100%;
        height: 100%;
        padding: 2em 2em 1em 1.5em;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        line-height: 1;

        @media all and (max-width: 600px) {
            padding: 2em 0 1em;
            font-size: 0.8em;
        }

        &__title {
            font-size: 1.6em;
            margin: 0 0 0.5em;
        }

        &__scrollable-area {
            flex: 1;
            min-height: 0;
        }

        &__product-list {
            display: flex;
            flex-direction: column;
            row-gap: 1em;
            margin: 1em 0;
            padding-right: 0.5em;
        }

        &__product-item {
            padding: 1em;
            background-color: #fafafb;
            border: 1px solid #e5e5eb;
            border-radius: 8px;
            display: flex;
            column-gap: 1.5em;
            align-items: center;
        }

        &__product-image-block {
            min-width: 5em;
            width: 5em;
            height: 5em;
        }

        :global(&__product-image) {
            width: 100%;
            height: 100%;
        }

        &__product-title {
            margin: 0;
            font-size: 1.3em;
        }

        &__product-rating-input {
            padding-top: 1em;
        }

        &__actions {
            display: flex;
            justify-content: right;
            margin: 2em 0 1em;
        }
    }
</style>
